import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import type { DBLeadList, LeadListSource, LeadSegment } from "@/lib/db-types";
import type { LeadImportRow } from "@/lib/lead-csv";

export interface LeadListStats {
  list_id: string;
  total: number;
  open_count: number;
  locked_count: number;
  in_pipe_count: number;
  customer_count: number;
  parked_count: number;
  irrelevant_count: number;
  called_count: number;
}

/** Statistikk per liste (view lead_list_stats_v). Få rader, så ingen paginering. */
export function useLeadListStats(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["lead-list-stats"],
    queryFn: async (): Promise<LeadListStats[]> => {
      const { data, error } = await supabase.from("lead_list_stats_v").select("*");
      if (error) throw error;
      return (data ?? []) as LeadListStats[];
    },
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
  });
}

export interface ImportResult {
  inserted: number;
  skipped_customer: number;
  skipped_duplicate: number;
}

export interface CreateLeadListInput {
  name: string;
  source: Exclude<LeadListSource, "agent">;
  segment?: LeadSegment | null;
  priority: number;
  notes?: string | null;
}

/** Oppretter listen (admin). Aktør og created_by er innlogget bruker. */
export function useCreateLeadList() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateLeadListInput): Promise<DBLeadList> => {
      const name = input.name.trim();
      if (!name) throw new Error("Navn mangler");
      const { data, error } = await supabase
        .from("lead_lists")
        .insert({
          name,
          source: input.source,
          segment: input.segment ?? null,
          priority: input.priority,
          notes: input.notes?.trim() || null,
          curated_by: user?.id ?? null,
          created_by: user?.id ?? null,
        })
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data as DBLeadList;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "lead_lists");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateLeadList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: Partial<Pick<DBLeadList, "name" | "priority" | "is_active" | "notes" | "owner_id" | "script">> }) => {
      const { error } = await supabase.from("lead_lists").update(input.patch).eq("id", input.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await invalidateFor(qc, "lead_lists");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/**
 * Importerer CSV-rader via RPC import_leads (admin). `dryRun` teller nye, allerede
 * kunde og duplikater uten å skrive (forhåndsvisning).
 */
export function useImportLeadRows() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { listId: string | null; rows: LeadImportRow[]; dryRun?: boolean }): Promise<ImportResult> => {
      const { data, error } = await supabase.rpc("import_leads", {
        p_list_id: input.listId,
        p_rows: input.rows,
        p_dry_run: input.dryRun ?? false,
      });
      if (error) throw new Error(error.message);
      return data as ImportResult;
    },
    onSuccess: async (_res, input) => {
      if (input.dryRun) return;
      await invalidateFor(qc, "leads");
      await invalidateFor(qc, "lead_lists");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
